import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Ban, CheckCircle2, Clock3, MailCheck, RefreshCw, Send, ShieldAlert } from 'lucide-react'
import { outreachService } from '../../services/outreachService'

const deliveryStatuses = {
  submitting: { label: 'Odesílá se', className: 'bg-blue-100 text-blue-800', icon: Clock3 },
  accepted: { label: 'Přijato Mailgunem', className: 'bg-blue-100 text-blue-800', icon: MailCheck },
  delivered: { label: 'Doručeno serveru příjemce', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  temporary_failed: { label: 'Dočasný problém', className: 'bg-amber-100 text-amber-800', icon: Clock3 },
  permanent_failed: { label: 'Trvalé selhání', className: 'bg-red-100 text-red-800', icon: Ban },
  failed: { label: 'Poskytovatel odmítl', className: 'bg-red-100 text-red-800', icon: AlertCircle },
  unknown: { label: 'Výsledek neznámý', className: 'bg-orange-100 text-orange-900', icon: ShieldAlert },
  unsubscribed: { label: 'Odhlášeno', className: 'bg-slate-200 text-slate-800', icon: Ban },
  complained: { label: 'Označeno jako spam', className: 'bg-red-100 text-red-900', icon: Ban },
}

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' })
  : '—'

const StatusBadge = ({ status }) => {
  const details = deliveryStatuses[status] || { label: status, className: 'bg-slate-100 text-slate-700', icon: Clock3 }
  const Icon = details.icon
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${details.className}`}>
      <Icon className="h-3.5 w-3.5" />
      {details.label}
    </span>
  )
}

const DeliveryPanel = ({ contact, onContactUpdated }) => {
  const [config, setConfig] = useState(null)
  const [deliveries, setDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError] = useState('')

  const loadDeliveryState = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const [nextConfig, history] = await Promise.all([
        outreachService.getDeliveryConfig(),
        outreachService.getDeliveries(contact._id),
      ])
      setConfig(nextConfig)
      setDeliveries(history.deliveries || [])
    } catch (requestError) {
      console.error('Unable to load outreach delivery state:', requestError)
      setError(requestError.message || 'Stav doručování se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }, [contact._id])

  useEffect(() => {
    loadDeliveryState()
  }, [loadDeliveryState, contact.delivery?.status])

  const recipientAllowed = useMemo(() => {
    if (!config) return false
    if (config.mode === 'live') return true
    return (config.testRecipients || []).includes(contact.normalizedEmail)
  }, [config, contact.normalizedEmail])

  const hasInitialDelivery = deliveries.length > 0 || Boolean(contact.delivery)
  const latestDeliveryStatus = deliveries[0]?.status || contact.delivery?.status
  const retryableFailure = latestDeliveryStatus === 'failed'
  const canStartSend = contact.draft?.status === 'approved' && config?.configured && recipientAllowed && (!hasInitialDelivery || retryableFailure)

  const handleSend = async () => {
    if (!confirmed || !canStartSend) return
    try {
      setSending(true)
      setError('')
      const payload = await outreachService.sendApprovedDraft(contact._id, contact.draft.revision)
      onContactUpdated(payload.contact)
      setDeliveries((current) => [payload.delivery, ...current.filter((item) => item._id !== payload.delivery._id)])
      setConfirming(false)
      setConfirmed(false)
    } catch (requestError) {
      console.error('Unable to send approved outreach draft:', requestError)
      const message = requestError.message || 'E-mail se nepodařilo odeslat.'
      if (requestError.details?.delivery) {
        const failedDelivery = requestError.details.delivery
        setDeliveries((current) => [failedDelivery, ...current.filter((item) => item._id !== failedDelivery._id)])
      }
      setConfirming(false)
      setConfirmed(false)
      await loadDeliveryState()
      setError(message)
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="mt-6 border-t border-slate-200 pt-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-950">Ruční doručení</h3>
          <p className="mt-1 text-xs leading-5 text-slate-500">Jeden schválený úvodní e-mail. Žádná dávka ani automatické opakování.</p>
        </div>
        <button type="button" disabled={loading || sending} onClick={loadDeliveryState} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Obnovit stav
        </button>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {!loading && config && !config.configured && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          <div className="font-medium">Doručování zatím není aktivní</div>
          <p className="mt-1 text-xs leading-5">Čeká na ověřenou doménu a nastavení: {(config.missing || []).join(', ')}.</p>
        </div>
      )}

      {!loading && config?.configured && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className={`rounded-full px-2.5 py-1 font-medium ${config.mode === 'test' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
            {config.mode === 'test' ? 'Testovací režim' : 'Živý režim'}
          </span>
          <span>Odesílatel: {config.fromName} &lt;{config.fromEmail}&gt;</span>
          {config.mode === 'test' && !recipientAllowed && (
            <span className="font-medium text-amber-800">Tento příjemce není na testovacím allowlistu.</span>
          )}
        </div>
      )}

      {!hasInitialDelivery && contact.draft?.status !== 'approved' && contact.draft?.status !== 'sent' && (
        <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">Nejdřív návrh uložte a schvalte.</p>
      )}

      {(!hasInitialDelivery || retryableFailure) && contact.draft?.status === 'approved' && !confirming && (
        <button type="button" disabled={!canStartSend || loading || sending} onClick={() => setConfirming(true)} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50">
          <Send className="h-4 w-4" />
          {retryableFailure ? 'Opakovat odmítnuté odeslání' : 'Odeslat schválený e-mail'}
        </button>
      )}

      {confirming && (
        <div className="mt-4 rounded-xl border-2 border-red-200 bg-red-50 p-4">
          <div className="font-semibold text-red-950">{retryableFailure ? 'Finální potvrzení bezpečného opakování' : 'Finální potvrzení odeslání'}</div>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-[90px_1fr]">
            <dt className="text-red-700">Příjemce</dt><dd className="break-all font-medium text-red-950">{contact.email}</dd>
            <dt className="text-red-700">Předmět</dt><dd className="font-medium text-red-950">{contact.draft.subject}</dd>
          </dl>
          <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg bg-white/70 p-3 text-sm text-red-950">
            <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-0.5 h-4 w-4 rounded border-red-400 text-red-700 focus:ring-red-500" />
            <span>Zkontroloval jsem příjemce i finální text. Chci tento jeden e-mail {retryableFailure ? 'znovu odeslat po jednoznačném odmítnutí poskytovatelem' : 'skutečně odeslat'}.</span>
          </label>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="button" disabled={sending} onClick={() => { setConfirming(false); setConfirmed(false) }} className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-800 hover:bg-white">Zrušit</button>
            <button type="button" disabled={!confirmed || sending} onClick={handleSend} className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50">
              <Send className="h-4 w-4" />
              {sending ? 'Odesílám…' : retryableFailure ? 'Ano, bezpečně opakovat' : 'Ano, odeslat jeden e-mail'}
            </button>
          </div>
        </div>
      )}

      {deliveries.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Historie doručení</div>
          {deliveries.map((delivery) => (
            <div key={delivery._id} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <StatusBadge status={delivery.status} />
                <span className="text-xs text-slate-500">{formatDateTime(delivery.submittedAt || delivery.createdAt)}</span>
              </div>
              <div className="mt-2 text-sm font-medium text-slate-800">{delivery.subject}</div>
              <div className="mt-1 text-xs text-slate-500">{delivery.mode === 'test' ? 'Testovací' : 'Živý'} režim · revize {delivery.draftRevision} · pokus {delivery.attemptCount || 1}</div>
              {delivery.providerError?.message && <div className="mt-2 text-xs text-red-700">{delivery.providerError.message}</div>}
              {delivery.status === 'unknown' && (
                <div className="mt-2 flex items-start gap-2 text-xs font-medium text-orange-900"><ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />Neodesílejte znovu. Nejprve ověřte stav podle Mailgun message ID.</div>
              )}
              {delivery.providerMessageId && <div className="mt-2 break-all font-mono text-[10px] text-slate-400">{delivery.providerMessageId}</div>}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

export default DeliveryPanel
